/**
 * @description The public contact numbers, and the helpers that render them.
 *
 * There are two, one per channel: WhatsApp conversations and inbound calls land on separate lines in
 * production, so a `wa.me` link and a `tel:` link must not be built from the same value. Outside
 * production both variables hold the same test line — the split is a production routing concern, and a
 * second unstaffed number on staging would only be a number nobody answers.
 *
 * Each is stored **unformatted** (bare digits, country code included, no `+`) and formatted at the point
 * of use, because the three shapes a number is needed in all differ:
 *
 * | Use | Shape |
 * |---|---|
 * | display | `+40 373 805 700` |
 * | `tel:` | `tel:+40373805700` |
 * | `wa.me` | `https://wa.me/40752199500` (no `+` — wa.me rejects it) |
 *
 * They are env vars rather than translation keys because the numbers differ per environment and a Loco
 * value is one string for every deployment. They are not locale-dependent either: the same numbers are
 * shown in every language.
 */

/**
 * Bare digits from the environment. Empty when unset, which every helper below treats as "no number",
 * so callers can decide whether to render the link at all rather than emitting a bare `+`.
 */
export const WHATSAPP_PHONE_NUMBER = process.env.NEXT_PUBLIC_WHATSAPP_PHONE_NUMBER ?? '';

/** As above, for the voice line. Distinct from {@link WHATSAPP_PHONE_NUMBER} in production. */
export const VOICE_PHONE_NUMBER = process.env.NEXT_PUBLIC_VOICE_PHONE_NUMBER ?? '';

/**
 * Digits only, tolerating a value that arrived with a `+`, spaces or dashes, and normalising a `00`
 * international prefix to none. Defensive: the env value is meant to be bare, but a stray character
 * must not produce `++40…` or silently drop a digit.
 */
const toDigits = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  return digits.startsWith('00') ? digits.slice(2) : digits;
};

/**
 * @description Human-readable form, e.g. `+40 752 199 500`.
 *
 * Groups Romanian numbers as `+40 XXX XXX XXX`. Anything it cannot group confidently falls back to a
 * plain `+<digits>` — a readable international number beats a wrongly-spaced one. Empty in, empty out.
 */
export const formatPhoneNumber = (value: string): string => {
  const digits = toDigits(value);
  if (!digits) return '';

  const romanian = /^40(\d{3})(\d{3})(\d{3})$/.exec(digits);
  if (romanian) return `+40 ${romanian[1]} ${romanian[2]} ${romanian[3]}`;

  return `+${digits}`;
};

/** @description Dial intent for an `href`. The handset places the call; we do not. */
export const phoneTelHref = (value: string): string => {
  const digits = toDigits(value);
  return digits ? `tel:+${digits}` : '';
};

/**
 * @description WhatsApp deep link, optionally with the first message already typed (the user still
 * sends it). `wa.me` wants bare digits — a leading `+` makes it 404.
 */
export const phoneWhatsAppHref = (value: string, message?: string): string => {
  const digits = toDigits(value);
  if (!digits) return '';

  const base = `https://wa.me/${digits}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
};

/**
 * Pre-formatted display value for the voice line — the common case at call sites, since the number the
 * site shows as text is always the one it invites you to ring. Nothing renders the WhatsApp number as
 * visible text, so there is deliberately no counterpart for it.
 */
export const voicePhoneDisplay = () => formatPhoneNumber(VOICE_PHONE_NUMBER);

/** `tel:` href for the voice line. Empty when unset, so callers can skip rendering. */
export const voicePhoneTelHref = () => phoneTelHref(VOICE_PHONE_NUMBER);

/** `wa.me` href for the WhatsApp line. Empty when unset, so callers can skip rendering. */
export const whatsAppHref = () => phoneWhatsAppHref(WHATSAPP_PHONE_NUMBER);
